/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, Suspense } from "react";
import "./App.css";
import { userRoutes } from "./routes/Routes";
import { Switch, BrowserRouter as Router } from "react-router-dom";
import { Route } from "react-router-dom/cjs/react-router-dom.min";
import { AppContext } from "./context/AppContext";
import Loader from "./components/loader/Loader";
import ErrorBoundary from "./components/error-boundary/ErrorBoundary";
import SupportSessionTimer from "./components/support-session-timer/SupportSessionTimer";
import UseAlert from "./components/alert/UseAlert";
import ApiService from "./utils/apiServices";
import { mockedRequestDetails, restrictedPaths } from "./utils/consts";
import { convertArrayToObj } from "./hooks/General";
import { rest_api_url } from "./constants/urls";
import "bootstrap/dist/css/bootstrap.min.css";
import "./components/modal/Modal.css";
import "./assets/styles/global-v2.css";
import useSocket from "./hooks/useSocket";
import "./assets/styles/mobile-data-rows.css";
import "./assets/styles/responsive-list-page.css";
import { fetchReportDownloadUrl } from "./pages/reporting/generated-reports/utils";
import { claimReport, setReportUserId } from "./utils/downloadReport";

function App() {
  const [isMobile, setIsMobile] = useState(window.document.documentElement.clientWidth <= 600);
  const [showSidebar, setShowSidebar] = useState(true);
  const [, alert_notify] = UseAlert();
  const [showNavMenu, setShowNavMenu] = useState(true);
  const [, setMenuPage] = useState("");
  const [isLoginPage, setIsLoginPage] = useState(false);
  const [user, setUser] = useState({});
  const [showClientsMenu, setShowClientsMenu] = useState(false);
  const [vendorsData, setVendorsData] = useState([]);
  const [archivedVendorsData, setArchivedVendorsData] = useState([]);
  const [filters, setFilters] = useState({});
  const [countries, setCountries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastReportUpdate, setLastReportUpdate] = useState(null);

  const [paymentRequestData, setPaymentRequestData] = useState({
    data: mockedRequestDetails,
  });
  const lsClientId = localStorage.getItem("client-id");

  useEffect(() => {
    if (restrictedPaths.includes(window.location.pathname)) {
      setIsLoginPage(true);
    } else {
      if (Object.keys(user).length !== 0) return;
      userData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginPage, lsClientId]);

  useEffect(() => {
    if (Object.keys(user).length !== 0 && !isLoginPage) {
      getVendors();
      getArchivedVendors();
      getFilters();
      getCountries();
      getRequestedData();
    }
    alert_notify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(true);
      setShowNavMenu(false);
    }
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "client-id") {
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(!isModalOpen);
    }
  }, [isModalOpen, isMobile]);

  const userData = () => {
    new Promise((resolve, reject) => {
      ApiService.get(rest_api_url.user_data, resolve, reject, true);
    })
      .then((response) => {
        localStorage.setItem("client-id", response.partner_id);
        setReportUserId(response.id);
        setUser(response);
        setIsLoginPage(false);
      })
      .catch((err) => {
        console.error("Could not load user data:", err?.message || err);
      });
  };

  const getVendors = async () => {
    let url = rest_api_url.back_office_vendors + "/for-user";

    if (user.role === "superadmin") {
      url = rest_api_url.back_office_vendors;
    }

    new Promise((resolve, reject) => {
      ApiService.get(url, resolve, reject, true);
    })
      .then((response) => {
        setVendorsData(response);
      })
      .catch((err) => {
        console.error("Could not load vendors:", err?.message || err);
      });
  };

  const getArchivedVendors = async () => {
    let url = rest_api_url.back_office_vendors + "/for-user/archived";
    if (user.role === "superadmin") url = rest_api_url.back_office_vendors + "/archived";

    new Promise((resolve, reject) => {
      ApiService.get(url, resolve, reject, true);
    })
      .then((response) => {
        setArchivedVendorsData(response);
      })
      .catch((err) => {
        console.error("Could not load archived vendors:", err?.message || err);
      });
  };

  const resizeHandler = () => {
    setIsMobile(window.document.documentElement.clientWidth <= 600);
  };
  const getFilters = () => {
    new Promise((resolve, reject) => {
      ApiService.get(rest_api_url.back_office_general + "/filters", resolve, reject, true);
    })
      .then((response) => {
        let data = { ...response };
        let paymentTypesFilter = [];
        Object.keys(response.payment_types).forEach((key) => {
          let obj = {
            name: response.payment_types[key].name,
            value: response.payment_types[key].key,
          };
          paymentTypesFilter.push(obj);
        });
        data.payment_types = paymentTypesFilter;
        setFilters(data);
        setIsLoginPage(false);
      })
      .catch((err) => {
        console.error("Could not load filters:", err?.message || err);
      });
  };
  const getCountries = async () => {
    new Promise((resolve, reject) => {
      ApiService.get(rest_api_url.back_office_general + "/countries", resolve, reject, true);
    })
      .then((response) => {
        let result = response;
        let convertedCountries = convertArrayToObj({
          arr: result,
          key: "cca2",
        });
        setCountries(convertedCountries);
      })
      .catch((err) => {
        alert_notify("error", err.message);
      });
  };

  const getRequestedData = async (data) => {
    try {
      new Promise((resolve, reject) => {
        ApiService.get(rest_api_url.payment_request_data, resolve, reject, true);
      })
        .then((response) => {
          if (response && Object.keys(response).length !== 0) {
            setPaymentRequestData({ data: JSON.parse(response) });
          }
        })
        .catch((err) => {
          alert_notify("error", err.message);
        });
    } catch (e) {
      alert_notify("error", e?.message);
    }
  };

  const handleReportReady = (data, currentUser) => {
    if (data?.status === "Failed") {
      alert_notify("error", `Report for ${data?.title} failed.`);
    } else {
      alert_notify(
        "info",
        <>
          Report for <strong>{data?.title}</strong> is ready for download.{" "}
          <a href="/generated-reports" target="_blank" rel="noopener noreferrer">
            Go to Generated Reports
          </a>
        </>,
        "",
        5000,
      );
      if (String(data?.userId) === String(currentUser?.id) && claimReport(data?.reportId)) {
        fetchReportDownloadUrl({
          reportId: data?.reportId,
          alert_notify,
        });
      }
    }
    return data;
  };

  useSocket((data) => {
    if (user && user?.id) {
      const updatedFields = handleReportReady(data, user);
      setLastReportUpdate(updatedFields);
    } else {
      setLastReportUpdate(data);
    }
  });

  useEffect(() => {
    if (user?.id && lastReportUpdate && !lastReportUpdate?.processed) {
      const updated = handleReportReady(lastReportUpdate, user);
      setLastReportUpdate({ ...updated, processed: true });
    }
  }, [user, lastReportUpdate]);

  return (
    <AppContext.Provider
      value={{
        user: user || null,
        countries: countries || null,
        isMobile: isMobile,
        showSidebar: showSidebar,
        setShowSidebar: setShowSidebar,
        showNavMenu: showNavMenu,
        setShowNavMenu: setShowNavMenu,
        setMenuPage: setMenuPage,
        setIsLoginPage: setIsLoginPage,
        setShowClientsMenu: setShowClientsMenu,
        showClientsMenu: showClientsMenu,
        vendorsData: vendorsData,
        setVendorsData: setVendorsData,
        archivedVendorsData: archivedVendorsData,
        setArchivedVendorsData: setArchivedVendorsData,
        setIsModalOpen,
        filters: filters,
        alert_notify: alert_notify,
        paymentRequestData,
        lastReportUpdate,
      }}
    >
      <Router>
        <SupportSessionTimer />
        {!user?.id && !isLoginPage ? (
          <Loader />
        ) : (
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <Switch>
                {userRoutes.map((route, index) => (
                  <Route path={route.path} component={route.component} key={index} />
                ))}
              </Switch>
            </Suspense>
          </ErrorBoundary>
        )}
      </Router>
    </AppContext.Provider>
  );
}

export default App;
